/**
 * estadoEfectivo — qué estado se está filtrando de verdad, en UN sitio.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * POR QUÉ EXISTE ESTE FICHERO
 *
 * El filtro de Estado del listado de Personas **no nace vacío**: nace en «Alta». Y ese defecto
 * vivía dentro de la función que construye la petición del listado, así que **sólo lo conocía el
 * listado**. El botón de exportar leía `estado` de la URL en crudo y obtenía `null`.
 *
 * Resultado, medido en producción:
 *
 *     sin tocar nada    pantalla 223   ·   Excel 274    el Excel traía de más
 *     con «Todos»       pantalla 274   ·   Excel 0      el Excel salía VACÍO
 *
 * El segundo es el peor: **un fichero sin filas se lee como «no hay datos», no como un fallo**.
 *
 * Es el tercer fallo de la misma forma esta semana —el ámbito del embudo, el orden del embudo y
 * ahora esto—: el fichero sale por una vía distinta de la pantalla. Y la diferencia con los dos
 * anteriores es que aquí nadie se olvidó de pasar un parámetro: es que **la regla sólo estaba
 * escrita en uno de los dos caminos**.
 *
 * ── Y POR QUÉ NO SE ESCRIBE EN LA URL, QUE FUE EL PRIMER INTENTO ─────────────────────────────
 *
 * La primera versión hacía que el listado **escribiera** el estado efectivo en la URL, para que no
 * hubiera regla que compartir. Dio un **bucle infinito** en TEST: el efecto de carga reconstruye la
 * URL después de un `await`, con el valor de `estado` capturado en su cierre —el de antes—, así que
 * lo quitaba; y esta escritura lo volvía a poner. Dos escritores peleando por el mismo parámetro.
 *
 * Y se construyó **para un beneficio que no existía**. El argumento era que compartir el enlace daba
 * una lista distinta de la que se veía. Medido después: **no**. La pantalla resuelve el defecto de
 * forma determinista, así que cualquier pestaña que abra la misma URL resuelve lo mismo. El defecto
 * **no es una elección del usuario**: es el mismo para todos, y no hay nada que recordar.
 *
 * Así que el escritor desapareció entero. El bucle no puede volver porque **ya no hay dos
 * escritores**, no porque se haya estabilizado uno.
 *
 * ── EL VOCABULARIO ───────────────────────────────────────────────────────────────────────────
 *
 *     ausente       no se ha elegido      ->  el defecto: Alta
 *     '<id>'        ese estado
 *     'todos'       todas las situaciones ->  **sin filtro**, que se expresa como AUSENCIA
 *
 * `'todos'` se traduce a «no mandes nada» y no a un valor nuevo, porque «no mandes nada» es lo que
 * los dos lados ya entienden. Inventar un tercer vocabulario para el servidor sería añadir una
 * traducción más donde ya se perdió una.
 */

import { puedeAbrirFichas } from '../../../providers/rolesFicha';

export const TODOS = 'todos';

/** El id de «Alta», BUSCADO POR NOMBRE. En las tres bases es el 3, y eso no se deduce: se mira. */
export function idDeAlta(estados) {
  return (estados || []).find(
    (e) => String(e?.nombre || '').trim().toLowerCase() === 'alta')?.Id ?? null;
}

/**
 * Lo que de verdad se está filtrando, a partir de lo que dice la URL.
 *
 * Devuelve `null` para «sin filtro» —que es como se manda al servidor— y nunca la cadena
 * `'todos'`, que es vocabulario de la URL y no del API.
 *
 * `rol`: quien no gestiona personas (rolesFicha.js) filtra SIEMPRE por «Alta», diga lo que diga la
 * URL. Uno de los estados es «Baja Medica», y el servidor (`filtroDeEstado`, hotfix 23/10) rechaza
 * cualquier otro para esos roles; la interfaz no les da el selector, y un `?estado=` escrito a mano
 * no se manda. Lo usan el listado y el botón de exportar, para que no vuelvan a divergir.
 */
export function estadoEfectivo(estadoEnURL, estados, rol) {
  if (!puedeAbrirFichas(rol)) return idDeAlta(estados);
  if (estadoEnURL === TODOS) return null;
  return estadoEnURL ?? idDeAlta(estados);
}
